/**
 * 秋田県の料理12品。レベルが上がるほど大きく、器つきの汁物・鍋物になる。
 *
 * 選定のときに迷った点を残しておく。
 * - 「笹巻き」は山形に、「干し餅」は青森にすでに置いてあるので、秋田からは外した。
 * - 「いものこ汁」は岩手の「いもの子汁」と名前が重なるため、汁物の枠は納豆汁とじゅんさい鍋に譲った。
 * - 「だまこ鍋」と「きりたんぽ鍋」は出汁も具もほぼ同じと公的な出典が書いている。
 *   串に刺して焼くきりたんぽのほうを採った。
 * - 「しょっつる鍋」はハタハタ寿しと材料が重なるので外した。ハタハタは一匹ずしの姿で入れている。
 * - 「稲庭うどん」はうどんが他県にも多いが、手延べで平たい極細の麺、ローラーでつぶす「つぶし」の
 *   工程、油を使わないという4点が公的な出典で確認できたので、讃岐などの角麺とは別物として入れた。
 * - 「赤漬け」はお盆に供えたのち食べる行事食である。由来には触れるが、供養の場面は描かない。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：かまぶく・豆腐カステラ・卵寒天・なた漬け・赤漬け・とんぶりの長芋和え（店で食べられる品に差し替え）
 * - 横手やきそば：「終戦直後に屋台のお好み焼き屋の男性が始めた」は文化庁資料にも「と言われています」とある伝承で、個人の特定にもつながるので書かない
 * - 横手やきそば：B-1グランプリの受賞歴・四天王などの店の称号は、料理の説明ではなく店や大会の話なので書かない
 * - 十文字中華そば：「昭和初期から続く」「昭和25、6年ごろから」など始まりの時期は、市の公式ページで確かめられなかったので書かない
 * - 十文字中華そば：麩をのせることは市の公式ページに書かれていないので、説明文には書かない
 * - 比内地鶏親子丼：「日本三大地鶏」は出典に見当たらないので書かない。出典（秋田県観光連盟）が書いている「日本三大美味鶏の一つ」だけを伝聞形で書いた
 * - 比内地鶏親子丼：卵の色・黄身をのせる盛り付け・甘辛の煮汁など作り方の細部は、公式資料で確かめられなかったので書かない（店ごとに作り方が違うと県も書いている）
 * - だまこ鍋：もとは魚を使っていて干拓で鶏に変わったという話は、字数の都合で書かない（書いてもよい事実）
 * - しょっつる鍋：農水省の資料にある「男鹿市が発祥地」は書いてもよいが、字数の都合で area にだけ反映した
 * - 男鹿の石焼き鍋：「昭和38年頃に男鹿温泉の宿（男鹿ホテル）が考案した」は、民間企業（株式会社男鹿なび）運営の「男鹿なび」にしか見当たらず、考案者・年を国や自治体の資料で確かめられないので書かない
 * - 男鹿の石焼き鍋：石の名（金石・溶結凝灰岩）や石が2〜3回で割れる話も同じページ（民間）にしかないので書かない
 * - 男鹿の石焼き鍋：男鹿温泉・入道崎の店で食べられるという案内も同じ理由で書かない
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';
import { akitaSceneryTour } from './akitaScenes';
import { AKITA_FOOD_HULLS } from './akitaFoodShapes.generated';

function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'iburigakko',
    level: 1,
    name: 'いぶりがっこ',
    shortName: 'いぶりがっこ',
    image: '/assets/foods/akita/01_iburigakko.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '「いぶり」はいぶした、「がっこ」は漬け物という秋田の方言。大根をいぶし小屋に吊るし、ナラや桜など広葉樹の煙で昼夜4〜5日いぶしてから、米ぬかに2か月以上漬けて熟成させる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/29_1_akita.html',
    icon: { label: 'いぶりがっこ', shape: 'dumpling', baseColor: '#a5641f', accentColor: '#e0b866' },
    reward: {
      unlockScore: 200,
      area: '横手市山内地域',
      headline: '煙でいぶしてから漬ける',
      tip: 'もとは家の囲炉裏の上で大根を干していたことが始まり。この地域のほぼ全戸で作られ、豪雪地の冬の食卓を支えた。起源は室町時代ともいわれる。',
      links: searchLinks('横手市', 'いぶりがっこ'),
    },
  },
  {
    id: 'yokote-yakisoba',
    level: 2,
    name: '横手やきそば',
    shortName: '横手やきそば',
    image: '/assets/foods/akita/02_yokote_yakisoba.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: '太くまっすぐな角麺のゆで麺を、豚ひき肉とキャベツと一緒に炒め、ダシ入りのソースで味をつける。福神漬けと半熟の目玉焼きを添えるのが親しまれている形。麺は1953年頃、市内の製麺業者との試行錯誤でできあがった。',
    source:
      'https://www.bunka.go.jp/seisaku/shokubunka/foodculture/assets/pdf/hyakunenfood/jirei/pdf_1152.pdf',
    icon: { label: '焼きそば', shape: 'noodle-bowl', baseColor: '#7a4a26', accentColor: '#f2c230' },
    reward: {
      unlockScore: 500,
      area: '横手市',
      headline: '角太のゆで麺に半熟の目玉焼き',
      tip: '最盛期には市内に100店ほどのやきそば店があった。いまも市内の約30店が出しており、その多くはほかの料理も出す「街食堂」だ。',
      links: searchLinks('横手市', '横手やきそば'),
      official: [{ label: '横手やきそば暖簾会「横手やきそばの特徴」', url: 'https://www.yokotekamakura.com/yokoteyakisoba/02_tokucyo/index.html' }, { label: '横手市観光協会「横手やきそば」', url: 'https://www.yokotekamakura.com/specialty-goods/6-1/' }],
    },
  },
  {
    id: 'hatahata-zushi',
    level: 3,
    name: 'ハタハタ寿し',
    shortName: 'ハタハタ寿し',
    image: '/assets/foods/akita/03_hatahata_zushi.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: '桶に笹、麹ご飯、ハタハタ、にんじん、ふのり、ゆずの順に重ねて発酵させる。ハタハタなしでは正月を迎えられないと言われるほどで、大みそかや正月の供え物に一匹ずしを用いる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/29_2_akita.html',
    icon: { label: 'ハタハタ寿し', shape: 'fish', baseColor: '#e4dcc8', accentColor: '#d8913a' },
    reward: {
      unlockScore: 1000,
      area: '男鹿市／八峰町／にかほ市',
      headline: '魚を一匹まるごと漬ける',
      tip: 'ハタハタの名は、雷光の古語「霹靂神（はたはたがみ）」に由来し、別名をカミナリウオという。民謡「秋田音頭」にも唄われる。',
      links: searchLinks('男鹿市', 'ハタハタ寿し'),
    },
  },
  {
    id: 'jumonji-chuka-soba',
    level: 4,
    name: '十文字中華そば',
    shortName: '十文字中華そば',
    image: '/assets/foods/akita/04_jumonji_chuka_soba.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: '煮干しや鰹節などでだしをとった、あっさりした醤油味の和風スープに、細い縮れ麺を合わせる。横手市十文字の中華そばで、市は横手やきそばと並ぶ「横手の麺王」と紹介している。',
    source:
      'https://www.city.yokote.lg.jp/kanko/1001536/1004602.html',
    icon: { label: '中華そば', shape: 'ramen', baseColor: '#d9b25f', accentColor: '#8a5a2b' },
    reward: {
      unlockScore: 1800,
      area: '横手市十文字町',
      headline: '煮干しだしのあっさり醤油味',
      tip: 'かつてはおやつ代わりに食べられていたという。大盛りを頼んでもまだ食べられそうな、軽い一杯だと市は紹介している。',
      links: searchLinks('横手市十文字町', '十文字中華そば'),
      official: [{ label: '横手市公式サイト「十文字の中華そば」', url: 'https://www.city.yokote.lg.jp/kanko/1001536/1004602.html' }],
    },
  },
  {
    id: 'natto-jiru',
    level: 5,
    name: '納豆汁',
    shortName: '納豆汁',
    image: '/assets/foods/akita/05_natto_jiru.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: 'すり鉢ですりつぶした納豆を混ぜた味噌汁。里芋、油揚げ、きのこ、わらび、豆腐が入る。県南の内陸部では正月といえば雑煮ではなくこれで、大きな鍋で作って三が日のあいだ温め直して食べる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/29_5_akita.html',
    icon: { label: '納豆汁', shape: 'rice-bowl', baseColor: '#c9ab6a', accentColor: '#4c6b2f' },
    reward: {
      unlockScore: 3000,
      area: '横手市／由利本荘市',
      headline: '正月は雑煮ではなくこれ',
      tip: '後三年の役（1083〜1087年）のころ、農民が兵糧としてわらに包んで届けた煮豆が糸を引く豆に変わったのが始まりという言い伝えがあり、横手市の金沢公園に記念碑が建つ。',
      links: searchLinks('横手市', '納豆汁'),
    },
  },
  {
    id: 'hinai-jidori-oyakodon',
    level: 6,
    name: '比内地鶏親子丼',
    shortName: '比内地鶏親子丼',
    image: '/assets/foods/akita/06_hinai_jidori_oyakodon.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '秋田を代表する食材、比内地鶏を使った親子丼。比内地鶏は鶏本来の肉の旨みと濃厚な肉質が味わえる鶏で、焼き鳥やきりたんぽ鍋とともに親子丼に欠かせないとされる。日本三大美味鶏の一つに数えられる。',
    source:
      'https://akita-fun.jp/featured_contents/gourmet',
    icon: { label: '親子丼', shape: 'rice-bowl', baseColor: '#f0b429', accentColor: '#e06a1b' },
    reward: {
      unlockScore: 4500,
      area: '大館市ほか県北部',
      headline: '比内地鶏でつくる親子丼',
      tip: '県の比内地鶏ネットは親子丼の統一レシピを載せていない。親子丼の専門店があるなど、店ごとに作り方を工夫しているためだという。',
      links: searchLinks('大館市', '比内地鶏 親子丼'),
      official: [{ label: '秋田県 比内地鶏ネット', url: 'https://common3.pref.akita.lg.jp/hinaijidori/' }],
    },
  },
  {
    id: 'damako-nabe',
    level: 7,
    name: 'だまこ鍋',
    shortName: 'だまこ鍋',
    image: '/assets/foods/akita/07_damako_nabe.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: '少しかために炊いたご飯をすりこぎで5割ほどつき、熱いうちに丸めて団子にする。これを鶏がらベースの汁で、鶏肉、ねぎ、せり、まいたけ、ごぼうと一緒に煮込む。八郎潟周辺の南秋地方が発祥地とされる冬の鍋。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/29_4_akita.html',
    icon: { label: 'だまこ鍋', shape: 'hotpot', baseColor: '#e8d9a8', accentColor: '#3f8f3a' },
    reward: {
      unlockScore: 6500,
      area: '八郎潟周辺（南秋地域）ほか県沿岸北部',
      headline: 'お手玉の形をしたご飯の団子',
      tip: '名は、秋田の言葉でお手玉を「だまこ」と呼ぶことから。新米を炊いてつぶし、団子にして食べる「庭あらい」という風習もある。',
      links: searchLinks('五城目町', 'だまこ鍋'),
      official: [{ label: 'アキタファン「秋田の食と酒」（だまこ鍋）', url: 'https://akita-fun.jp/featured_contents/gourmet' }],
    },
  },
  {
    id: 'inaniwa-udon',
    level: 8,
    name: '稲庭うどん',
    shortName: '稲庭うどん',
    image: '/assets/foods/akita/08_inaniwa_udon.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '手延べでつくる平たい極細のうどん。2本の棒に8の字を描くようにかける「手綯い」、熟成した生地をローラーでつぶす「つぶし」、桁にかけて手でさすりながら延ばす「延ばし」の3工程を経る。そうめんと違って油を使わない。',
    source:
      'https://www.maff.go.jp/j/pr/aff/1607/n_search.html',
    icon: { label: '稲庭うどん', shape: 'noodle-bowl', baseColor: '#f4efe2', accentColor: '#4a3c2c' },
    reward: {
      unlockScore: 9000,
      area: '湯沢市稲庭',
      headline: '平たい極細のストレート麺',
      tip: '寛文5年（1665年）に製法が確立し、宝暦2年（1752年）には秋田藩主佐竹侯の御用達となった。昭和47年に七代目佐藤養助が製法を公開した。',
      links: searchLinks('湯沢市', '稲庭うどん'),
    },
  },
  {
    id: 'junsai-nabe',
    level: 9,
    name: 'じゅんさい鍋',
    shortName: 'じゅんさい鍋',
    image: '/assets/foods/akita/09_junsai_nabe.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: 'じゅんさいは、沼や池に丸い葉を浮かべる水草。茎から出るゼリー状の新芽を食べる。鶏肉やごぼうと煮て、最後にじゅんさいを入れ、色が緑になったら盛る。秋田では夏の鍋として親しまれている。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/29_16_akita.html',
    icon: { label: 'じゅんさい鍋', shape: 'hotpot', baseColor: '#3f6b3a', accentColor: '#e8e2cc' },
    reward: {
      unlockScore: 12000,
      area: '三種町',
      headline: '夏に食べる鍋',
      tip: '沼に縄のように細長く生えることから「ヌナワ（沼縄）」と呼ばれた。三種町では箱舟に乗ってひとつひとつ手で摘む作業が夏の風物詩になっている。',
      links: searchLinks('三種町', 'じゅんさい鍋'),
    },
  },
  {
    id: 'shottsuru-nabe',
    level: 10,
    name: 'しょっつる鍋',
    shortName: 'しょっつる鍋',
    image: '/assets/foods/akita/10_shottsuru_nabe.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '魚を塩漬けにして1年以上寝かせた魚醤「しょっつる」で味をととのえ、ハタハタとねぎ、豆腐、白菜、せりなどを煮る。しょっつるは、醤油が高級品だった時代にその代わりの調味料として生まれた。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/29_23_akita.html',
    icon: { label: 'しょっつる', shape: 'hotpot', baseColor: '#c99a4b', accentColor: '#7c8a96' },
    reward: {
      unlockScore: 16000,
      area: '男鹿市ほか県沿岸部',
      headline: '魚醤で煮るハタハタの鍋',
      tip: 'ハタハタは、海が荒れて雷が鳴るような時に、産卵のため大群で近海に現れる。とれる11〜12月をはじめ、冬のあいだに食べる。',
      links: searchLinks('男鹿市', 'しょっつる鍋'),
    },
  },
  {
    id: 'oga-ishiyaki',
    level: 11,
    name: '男鹿の石焼き鍋',
    shortName: '男鹿の石焼き',
    image: '/assets/foods/akita/11_oga_ishiyaki.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: 'ぶつ切りにした魚介を木の桶に入れ、熱した男鹿の石を放り込んで一気に煮立てる。味は味噌。漁師たちの調理方法がもとになった、男鹿の海の幸を熱いうちに味わう料理。',
    source:
      'https://akita-fun.jp/featured_contents/gourmet',
    icon: { label: '石焼', shape: 'pot', baseColor: '#b98a5a', accentColor: '#e0442b' },
    reward: {
      unlockScore: 22000,
      area: '男鹿市',
      headline: '焼いた石で桶の汁を煮立てる',
      tip: '鍋を火にかけるのではなく、熱した石の熱だけで汁を沸かすのが特徴。秋田県の公式観光サイトは「石焼料理」の名で紹介している。',
      links: searchLinks('男鹿市', '男鹿 石焼料理'),
    },
  },
  {
    id: 'kiritanpo-nabe',
    level: 12,
    name: 'きりたんぽ鍋',
    shortName: 'きりたんぽ鍋',
    image: '/assets/foods/akita/12_kiritanpo_nabe.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: 'つぶしたご飯を棒に刺して焼いた「たんぽ」を、鍋に入る長さに切って鶏の出汁で煮る。炭焼きや伐採で山に籠った人が、残り飯をつぶして棒に刺して焼き、鳥鍋に入れたのが始まりとされる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/29_6_akita.html',
    icon: { label: 'きりたんぽ鍋', shape: 'pot', baseColor: '#d9b878', accentColor: '#3f5c28' },
    reward: {
      unlockScore: 30000,
      area: '大館市／鹿角市',
      headline: '「短穂」から来た名前',
      tip: '串に刺して焼いたご飯ががまの穂に似ており、短い穂の意味の「短穂」から「たんぽ」と呼ばれるようになった。新米がとれる時期に作られることが多い。',
      links: searchLinks('大館市', 'きりたんぽ鍋'),
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const akitaFoodStage: StageDefinition = {
  regionId: 'akita',
  regionName: '秋田県',
  mode: 'food',
  title: '秋田めしマージ',
  theme: {
    primaryColor: '#a8471d',
    secondaryColor: '#efe4d1',
    accentColor: '#d6a33e',
    backgroundColor: '#fbf5ea',
  },
  /** 背景ツアー「秋田めぐり」。軸は季節。4月の角館から始めて、2月の小安峡で終わる。 */
  scenery: {
    tour: akitaSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, AKITA_FOOD_HULLS),
};
